import { useState, type Dispatch, type SetStateAction } from "react";
import Button from "../../../shared/components/Button";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type {
  ChallengeCategoryCode,
  ChallengeCategoryGender,
  ChallengeDetail,
} from "../../challenges/types/challengeAdmin";
import {
  categoryGenderLabel,
  categoryLabel,
} from "../../challenges/utils/challengeLabels";
import type { TournamentRegistrationCategoryItem } from "../utils/tournamentCategories";
import {
  updateChallengeMatchStatus,
  updateTournamentParticipantCheckin,
  type ParticipantCheckin,
} from "../services/eventRegistrationAdminService";
import { mergeChallengeRoundsPreservingLocalScores } from "../utils/challengeRoundsMerge";
import type { MatchCheckInSide } from "./AdminMatchCheckInControl";
import AdminChallengeRoundsEditor from "./AdminChallengeRoundsEditor";
import AdminTournamentOperationsPanel from "./AdminTournamentOperationsPanel";

type WinnerSide = "dupla1" | "dupla2" | "";
type SaveChallengeOptions = {
  includeRounds?: boolean;
};

type AdminFinishedChallengePanelProps = {
  activeTournamentCategoryKey: string;
  categoryOptions: ChallengeCategoryCode[];
  challengeDetail: ChallengeDetail;
  categories: TournamentRegistrationCategoryItem[];
  genderOptions: ChallengeCategoryGender[];
  isAdmin: boolean;
  isPoco: boolean;
  isSuper8: boolean;
  isTournamentChallenge: boolean;
  onBackToResults: () => void;
  onSaveChallenge: (options?: SaveChallengeOptions) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onUpdatePocoWinner: (
    roundIndex: number,
    matchIndex: number,
    winnerSide: WinnerSide,
  ) => void;
  onUpdateRoundScore: (
    roundIndex: number,
    matchIndex: number,
    field: "score1" | "score2",
    value: string,
  ) => void;
  pairLabelById: Record<string, string>;
  resolveRoundPairLabelWithPoints: (
    pairId: string,
    roundIndex: number,
  ) => string;
  rounds: any[];
  savingChallenge: boolean;
  scoreMax: number;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setChallengeRoundsJson: Dispatch<SetStateAction<string>>;
  token: string;
};

export default function AdminFinishedChallengePanel({
  activeTournamentCategoryKey,
  categoryOptions,
  challengeDetail,
  categories,
  genderOptions,
  isAdmin,
  isPoco,
  isSuper8,
  isTournamentChallenge,
  onBackToResults,
  onError,
  onSaveChallenge,
  onSuccess,
  onUpdatePocoWinner,
  onUpdateRoundScore,
  pairLabelById,
  resolveRoundPairLabelWithPoints,
  rounds,
  savingChallenge,
  scoreMax,
  setChallengeDetail,
  setChallengeRoundsJson,
  token,
}: AdminFinishedChallengePanelProps) {
  const t = siteTexts.admin;
  const [pendingCheckInSideId, setPendingCheckInSideId] = useState<string | null>(null);
  const [pendingCourtMatchId, setPendingCourtMatchId] = useState<number | null>(null);

  const normalizeCpf = (value: string | null | undefined) =>
    String(value || "").replace(/\D/g, "").slice(0, 11);

  const replaceCheckins = (list: ParticipantCheckin[]) => {
    setChallengeDetail((current) =>
      current ? { ...current, participantCheckins: list } : current,
    );
  };

  const buildOptimisticCheckins = (
    current: ParticipantCheckin[],
    cpfs: string[],
    checkedIn: boolean,
  ) => {
    const now = new Date().toISOString();
    const map = new Map<string, ParticipantCheckin>();
    current.forEach((item) => {
      const cpf = normalizeCpf(item.cpf);
      if (cpf) map.set(cpf, item);
    });
    cpfs.forEach((cpf) => {
      if (checkedIn) {
        map.set(cpf, {
          cpf,
          checkedIn: true,
          checkedInAt: now,
        });
      } else {
        map.delete(cpf);
      }
    });
    return Array.from(map.values());
  };

  const toggleCheckInSide = async (
    side: MatchCheckInSide,
    checkedIn: boolean,
  ) => {
    const cpfs = Array.from(
      new Set(side.participantCpfs.map(normalizeCpf).filter((cpf) => cpf.length >= 8)),
    );
    if (cpfs.length === 0 || pendingCheckInSideId) return;
    const previous = challengeDetail.participantCheckins || [];
    setPendingCheckInSideId(side.id);
    replaceCheckins(buildOptimisticCheckins(previous, cpfs, checkedIn));
    try {
      let next = previous;
      for (const cpf of cpfs) {
        const response = await updateTournamentParticipantCheckin(
          token,
          Number(challengeDetail.id),
          cpf,
          checkedIn,
        );
        next = response.participantCheckins || [];
      }
      replaceCheckins(next);
    } catch (error) {
      replaceCheckins(previous);
      onError(
        error instanceof Error
          ? error.message
          : checkedIn
            ? siteTexts.challengeEngine.checkIn.confirmError
            : siteTexts.challengeEngine.checkIn.undoError,
      );
    } finally {
      setPendingCheckInSideId(null);
    }
  };

  const updateRoundsFromApi = (nextRounds: unknown[][]) => {
    if (!Array.isArray(nextRounds)) return;
    const mergedRounds = mergeChallengeRoundsPreservingLocalScores(
      nextRounds,
      rounds,
    );
    setChallengeRoundsJson(JSON.stringify(mergedRounds, null, 2));
    setChallengeDetail((current) =>
      current
        ? {
            ...current,
            rounds: mergedRounds as ChallengeDetail["rounds"],
          }
        : current,
    );
  };

  const toggleCourtStatus = async (
    _roundIndex: number,
    _matchIndex: number,
    match: any,
    status: "pending" | "scheduled" | "in_progress",
  ) => {
    const matchId = Number(match?.id || 0);
    if (!matchId || pendingCourtMatchId) return;
    setPendingCourtMatchId(matchId);
    try {
      const response = await updateChallengeMatchStatus(
        token,
        Number(challengeDetail.id),
        matchId,
        status,
      );
      updateRoundsFromApi(response.rounds || []);
      onSuccess(
        status === "in_progress"
          ? "Partida colocada em quadra."
          : "Partida retirada da quadra.",
      );
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : siteTexts.challengeEngine.courtStatus.updateError,
      );
    } finally {
      setPendingCourtMatchId(null);
    }
  };
  const createTexts = t.createChallengePage;

  return (
    <>
      {!isTournamentChallenge && (
        <div className="form-grid admin-edit-form-grid">
          <div className="admin-row-two">
            <label className="field">
              <span>{createTexts.gender}</span>
              <select
                value={challengeDetail.categoryGender || "mixed"}
                onChange={(event) => {
                  const nextGender = event.target.value as ChallengeCategoryGender;
                  setChallengeDetail((prev) =>
                    prev ? { ...prev, categoryGender: nextGender } : prev,
                  );
                }}
              >
                {genderOptions.map((gender) => (
                  <option key={`edit-finished-gender-${gender}`} value={gender}>
                    {categoryGenderLabel(gender)}
                  </option>
                ))}
              </select>
            </label>

            {(challengeDetail.categoryGender || "mixed") === "mixed" ||
            (challengeDetail.categoryGender || "mixed") === "open" ? (
              <>
                <label className="field">
                  <span>{createTexts.maleCategory}</span>
                  <select
                    value={challengeDetail.categoryMale || "6a"}
                    onChange={(event) => {
                      const nextCategory = event.target
                        .value as ChallengeCategoryCode;
                      setChallengeDetail((prev) =>
                        prev
                          ? { ...prev, categoryMale: nextCategory }
                          : prev,
                      );
                    }}
                  >
                    {categoryOptions.map((category) => (
                      <option
                        key={`edit-finished-category-m-${category}`}
                        value={category}
                      >
                        {categoryLabel(category)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{createTexts.femaleCategory}</span>
                  <select
                    value={challengeDetail.categoryFemale || "6a"}
                    onChange={(event) => {
                      const nextCategory = event.target
                        .value as ChallengeCategoryCode;
                      setChallengeDetail((prev) =>
                        prev
                          ? { ...prev, categoryFemale: nextCategory }
                          : prev,
                      );
                    }}
                  >
                    {categoryOptions.map((category) => (
                      <option
                        key={`edit-finished-category-f-${category}`}
                        value={category}
                      >
                        {categoryLabel(category)}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <label className="field">
                <span>{createTexts.category}</span>
                <select
                  value={challengeDetail.category || "6a"}
                  onChange={(event) => {
                    const nextCategory = event.target
                      .value as ChallengeCategoryCode;
                    setChallengeDetail((prev) =>
                      prev ? { ...prev, category: nextCategory } : prev,
                    );
                  }}
                >
                  {categoryOptions.map((category) => (
                    <option
                      key={`edit-finished-category-${category}`}
                      value={category}
                    >
                      {categoryLabel(category)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {isAdmin && (
            <label className="field">
              <span>
                {Boolean(challengeDetail.isOfficial ?? true)
                  ? createTexts.includeRanking
                  : createTexts.includeOrganizerRanking}
              </span>
              <select
                value={
                  Boolean(challengeDetail.includeInRanking ?? true)
                    ? "yes"
                    : "no"
                }
                onChange={(event) => {
                  const next = event.target.value === "yes";
                  setChallengeDetail((prev) =>
                    prev ? { ...prev, includeInRanking: next } : prev,
                  );
                }}
              >
                <option value="yes">{createTexts.yes}</option>
                <option value="no">{createTexts.no}</option>
              </select>
            </label>
          )}

          <AdminChallengeRoundsEditor
            challengeDetail={challengeDetail}
            emptyMessage="Nenhuma rodada disponível para edição."
            finalLabel={t.historyList.final}
            isPoco={isPoco}
            isSuper8={isSuper8}
            isTournamentChallenge={false}
            onToggleCheckInSide={(side, checkedIn) =>
              void toggleCheckInSide(side, checkedIn)
            }
            onToggleCourtStatus={(roundIndex, matchIndex, match, status) =>
              void toggleCourtStatus(roundIndex, matchIndex, match, status)
            }
            onUpdatePocoWinner={onUpdatePocoWinner}
            onUpdateRoundScore={onUpdateRoundScore}
            pairLabelById={pairLabelById}
            participantCheckins={challengeDetail.participantCheckins || []}
            pendingCheckInSideId={pendingCheckInSideId}
            pendingCourtMatchId={pendingCourtMatchId}
            participantsCountForGroup={Number(
              challengeDetail.participantsCount || 0,
            )}
            resolveRoundPairLabelWithPoints={resolveRoundPairLabelWithPoints}
            resultsOnly
            rounds={rounds}
            scoreMax={scoreMax}
            courtsCount={challengeDetail.courtsCount}
          />
        </div>
      )}

      {isTournamentChallenge && (
        <AdminTournamentOperationsPanel
          token={token}
          tournamentId={Number(challengeDetail.id)}
          categories={categories}
          activeCategoryKey={activeTournamentCategoryKey}
          onError={onError}
          onSuccess={onSuccess}
          tournamentStarted={
            Boolean(challengeDetail.startedAt) ||
            challengeDetail.status === "started" ||
            challengeDetail.status === "finished"
          }
          tournamentFinished={challengeDetail.status === "finished"}
        />
      )}

      <div className="admin-modal-actions">
        {!isTournamentChallenge && (
          <Button type="button" variant="pill" onClick={onBackToResults}>
            Voltar aos resultados
          </Button>
        )}
        <Button
          type="button"
          variant="primary"
          onClick={() => onSaveChallenge()}
          disabled={savingChallenge}
        >
          {savingChallenge ? t.historyList.saving : t.historyList.saveResults}
        </Button>
      </div>
    </>
  );
}
